import { useState, useMemo, useCallback } from 'react';
import { UserPlus, Crown } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { PersonAvatar } from '../shared';
import { TeamMemberList } from './TeamMemberList';
import { ConfirmRemoveDialog } from '../dialogs';
import { skip } from '@signalvote/data';
import { useTeam, useRemoveTeamMember, useAddTeamMembers } from '@/hooks/queries/useTeams';
import { useVolunteers } from '@/hooks/queries';
import { staffUnreadyMessage } from '@/lib/staffRead';

interface TeamDetailPanelProps {
  teamId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamDetailPanel({ teamId, open, onOpenChange }: TeamDetailPanelProps) {
  const teamRead = useTeam(teamId ?? skip);
  const team = teamRead.status === 'ready' ? teamRead.data : undefined;
  const staffRead = useVolunteers(undefined);
  const allVolunteers = useMemo(() => (staffRead.status === 'ready' ? staffRead.data : []), [staffRead]);
  const { mutate: removeMember, isPending: removeMemberPending } = useRemoveTeamMember();
  const { mutate: addMembers, isPending: addMembersPending } = useAddTeamMembers();
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);

  // Volunteers not already in this team
  const availableVolunteers = useMemo(() => {
    if (!team?.members) return allVolunteers;
    const memberIds = new Set(team.members.map((m) => m.userId));
    return allVolunteers.filter((v) => !memberIds.has(v.userId));
  }, [allVolunteers, team?.members]);

  // Filter available volunteers by search
  const filteredAvailable = useMemo(() => {
    if (!addMemberSearch.trim()) return availableVolunteers.slice(0, 10);
    const q = addMemberSearch.toLowerCase();
    return availableVolunteers
      .filter(
        (v) =>
          v.firstName.toLowerCase().includes(q) ||
          v.lastName.toLowerCase().includes(q) ||
          v.email.toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [availableVolunteers, addMemberSearch]);

  const handleAddMember = (userId: string) => {
    if (!teamId) return;
    addMembers(
      { teamId, userIds: [userId] },
      {
        onSuccess: () => {
          setAddMemberSearch('');
        },
      }
    );
  };

  const handleRemoveMember = useCallback((userId: string) => {
    setRemoveMemberId(userId);
  }, []);

  const confirmRemoveMember = useCallback(() => {
    if (removeMemberId && teamId) {
      removeMember({ teamId, userId: removeMemberId }, {
        onSuccess: () => setRemoveMemberId(null),
      });
    }
  }, [removeMemberId, removeMember, teamId]);

  const removeMemberName = useMemo(() => {
    if (!removeMemberId || !team?.members) return '';
    const member = team.members.find((m) => m.userId === removeMemberId);
    return member ? `${member.user.firstName} ${member.user.lastName}` : '';
  }, [removeMemberId, team?.members]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {teamRead.status === 'failed' || teamRead.status === 'offline' ? (
          <p role="status" className="pt-6 text-sm text-muted-foreground">
            {teamRead.status === 'offline' ? "You're offline, so this team can't load." : "Couldn't load this team. Try again shortly."}
          </p>
        ) : !team ? (
          <div className="space-y-4 pt-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>{team.name}</SheetTitle>
              {team.description && (
                <SheetDescription>{team.description}</SheetDescription>
              )}
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Team Lead */}
              {team.lead && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-accent/50">
                  <Crown className="h-4 w-4 text-amber-500 shrink-0" />
                  <PersonAvatar
                    firstName={team.lead.firstName}
                    lastName={team.lead.lastName}
                    size="sm"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {team.lead.firstName} {team.lead.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">Team Lead</p>
                  </div>
                </div>
              )}

              <Separator />

              {/* Members section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">
                    Members ({team.members?.length ?? 0})
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setShowAddMember(!showAddMember)}
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>

                {/* Add member search */}
                {showAddMember && (
                  <div className="mb-3 space-y-2">
                    <Input
                      placeholder="Search people to add..."
                      value={addMemberSearch}
                      onChange={(e) => setAddMemberSearch(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                    />
                    {staffUnreadyMessage(staffRead) ? (
                      <p role="status" className="text-xs text-muted-foreground text-center py-2">
                        {staffUnreadyMessage(staffRead)}
                      </p>
                    ) : filteredAvailable.length > 0 ? (
                      <div className="border rounded-md max-h-[200px] overflow-y-auto">
                        {filteredAvailable.map((v) => (
                          <button
                            key={v.userId}
                            className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-accent text-sm"
                            onClick={() => handleAddMember(v.userId)}
                            disabled={addMembersPending}
                          >
                            <PersonAvatar
                              firstName={v.firstName}
                              lastName={v.lastName}
                              size="sm"
                            />
                            <div className="min-w-0">
                              <span className="truncate block text-sm">
                                {v.firstName} {v.lastName}
                              </span>
                              <span className="truncate block text-xs text-muted-foreground">
                                {v.email}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        {addMemberSearch ? 'No matching people found' : 'All people are already in this team'}
                      </p>
                    )}
                  </div>
                )}

                {/* Member list */}
                <TeamMemberList
                  members={team.members}
                  leadUserId={team.leadUserId}
                  onRemoveMember={handleRemoveMember}
                  isRemoving={removeMemberPending}
                />
              </div>
            </div>
          </>
        )}
      </SheetContent>

      {/* Confirm remove member */}
      <ConfirmRemoveDialog
        open={!!removeMemberId}
        onOpenChange={(open) => { if (!open) setRemoveMemberId(null); }}
        title="Remove Team Member"
        description={`Remove ${removeMemberName} from this team? They won't be deleted from the campaign.`}
        confirmLabel="Remove"
        onConfirm={confirmRemoveMember}
        isLoading={removeMemberPending}
      />
    </Sheet>
  );
}
